import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { analyticsEvents, track } from '@/lib/analytics/posthog';
import { OWNER_FOCUS_VALUES, type OwnerFocus } from '@/lib/personas/intent';

export type OwnerFocusSource = 'show_tools' | 'profile';

const parse = (value: unknown): OwnerFocus | null =>
  OWNER_FOCUS_VALUES.find((focus) => focus === value) ?? null;

/**
 * Whether the signed-in owner runs a show, makes sequences, or both. Unanswered reads as 'both',
 * so nobody who never answered loses a prompt. It has its own query, apart from useUserProfile,
 * so a read failure (the column is missing until its migration is applied) only costs this hook.
 * `ready` is true once the stored answer is known; the question only shows when ready and unanswered.
 */
export function useOwnerFocus() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const queryKey = ['ownerFocus', userId] as const;

  const query = useQuery({
    queryKey,
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<OwnerFocus | null> => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('owner_focus')
        .eq('user_id', userId as string)
        .maybeSingle();
      if (error) throw error;
      return parse(data?.owner_focus);
    },
  });

  const setFocus = useCallback(
    async (focus: OwnerFocus, source: OwnerFocusSource) => {
      if (!userId) throw new Error('Sign in first.');
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ owner_focus: focus })
        .eq('user_id', userId)
        .select('owner_focus');
      if (error) throw new Error(error.message);
      if (!data || data.length === 0) throw new Error('Could not find your profile.');
      queryClient.setQueryData(['ownerFocus', userId], focus);
      track(analyticsEvents.OWNER_FOCUS_SET, { focus, source });
    },
    [queryClient, userId],
  );

  const stored = query.isSuccess ? query.data : null;
  return {
    focus: stored ?? ('both' as OwnerFocus),
    answered: stored !== null,
    ready: query.isSuccess,
    setFocus,
  };
}
